import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

interface User {
  id: number;
  email: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionExpired: boolean;
  /** True after Logout until the next sign-in: the login page then carries no return page (FR-001f). */
  signedOut: boolean;
  storageError: string | null;
  login: (token: string) => void;
  logout: () => void;
  clearSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = 'bridge_auth_token';

interface JwtPayload {
  sub: string;
  userId?: number;
  email: string;
  role?: string;
  roles?: string[];
  exp: number;
  iss?: string;
}

/**
 * Checks if localStorage is available.
 * T044m: Handle browser storage unavailable edge case.
 */
function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/**
 * Safely parses a JWT token.
 * T044n: Handles corrupted/invalid tokens gracefully.
 */
function parseJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null; // Invalid JWT structure
    }

    const base64Url = parts[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    const payload = JSON.parse(jsonPayload) as JwtPayload;

    // Validate required fields
    if (!payload.sub || !payload.email || typeof payload.exp !== 'number') {
      return null;
    }

    // Validate issuer if present
    if (payload.iss && payload.iss !== 'partner-bridge') {
      return null;
    }

    return payload;
  } catch {
    return null; // T044n: Invalid/corrupted token
  }
}

function extractUserFromPayload(payload: JwtPayload): User {
  const userId = payload.userId ?? (payload.sub ? parseInt(payload.sub, 10) : 0);
  const roles = payload.roles ?? (payload.role ? [payload.role] : []);
  return {
    id: userId,
    email: payload.email,
    role: roles[0] ?? 'admin',
  };
}

/**
 * Checks if a token is expired.
 * T044k: Token expiry check for session timeout.
 */
function isTokenExpired(token: string): boolean {
  const payload = parseJwt(token);
  if (!payload) return true;
  // Add 5 second buffer to avoid edge cases
  return payload.exp * 1000 < Date.now() + 5000;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const [storageError, setStorageError] = useState<string | null>(null);

  /**
   * Clears auth state and removes session token.
   * Note: Remember 2FA token is intentionally NOT cleared on logout.
   * It persists for 14 days to skip 2FA on subsequent logins.
   */
  const clearAuth = useCallback((): void => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      // Do NOT remove bridge_remember_2fa_token - it should persist across logout
    } catch {
      // Ignore storage errors during cleanup
    }
    setUser(null);
  }, []);

  /**
   * T044l: Multi-tab logout synchronization via storage event listener.
   */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === TOKEN_KEY) {
        if (event.newValue === null) {
          // Token was removed in another tab - log out this tab
          setUser(null);
        } else if (event.newValue) {
          // Token was updated in another tab - update user
          const payload = parseJwt(event.newValue);
          if (payload && !isTokenExpired(event.newValue)) {
            setUser(extractUserFromPayload(payload));
          } else {
            clearAuth();
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => { window.removeEventListener('storage', handleStorageChange); };
  }, [clearAuth]);

  /**
   * T044k: Periodic token expiry check.
   * Checks every 30 seconds if the token is still valid.
   */
  useEffect(() => {
    if (!user) return;

    const checkExpiry = (): void => {
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token || isTokenExpired(token)) {
          setSessionExpired(true);
          clearAuth();
        }
      } catch {
        // Storage error - clear auth
        clearAuth();
      }
    };

    // Check immediately and then every 30 seconds
    const interval = setInterval(checkExpiry, 30000);

    return () => { clearInterval(interval); };
  }, [user, clearAuth]);

  /**
   * Initial auth state check on mount.
   * T044m: Check localStorage availability.
   * T044n: Handle corrupted tokens.
   */
  useEffect(() => {
    // T044m: Check localStorage availability
    if (!isLocalStorageAvailable()) {
      setStorageError(
        'Browser storage is not available. Please enable localStorage or use a different browser to continue.'
      );
      setIsLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem(TOKEN_KEY);

      if (token) {
        const payload = parseJwt(token);

        if (!payload) {
          // T044n: Invalid/corrupted token - clear and redirect
          localStorage.removeItem(TOKEN_KEY);
          setUser(null);
        } else if (isTokenExpired(token)) {
          // T044k: Token expired - clear and show message
          localStorage.removeItem(TOKEN_KEY);
          setSessionExpired(true);
          setUser(null);
        } else {
          // Valid token - set user
          setUser(extractUserFromPayload(payload));
        }
      }
    } catch {
      // Error reading storage - clear auth state
      setUser(null);
    }

    setIsLoading(false);
  }, []);

  const login = useCallback((token: string): void => {
    try {
      const payload = parseJwt(token);
      if (!payload) {
        throw new Error('Invalid token');
      }

      localStorage.setItem(TOKEN_KEY, token);
      setUser(extractUserFromPayload(payload));
      setSessionExpired(false);
      setSignedOut(false);
    } catch {
      // Failed to parse or store token
      setUser(null);
    }
  }, []);

  const logout = useCallback((): void => {
    clearAuth();
    setSessionExpired(false);
    setSignedOut(true);
  }, [clearAuth]);

  const clearSessionExpired = useCallback((): void => {
    setSessionExpired(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        sessionExpired,
        signedOut,
        storageError,
        login,
        logout,
        clearSessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
